import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

export const Header = () => {
  const { isAuthenticated, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <header className="header">
      <nav className="container-main header-inner">
        {/* Brand */}
        <Link to="/" className="header-brand focus-ring">
          <img
            src="https://cdn.ribbonstudios.net/web/Square_Transparent_White.png"
            alt=""
            width={32}
            height={32}
            style={{ height: '2rem', width: '2rem' }}
          />
          <span className="header-brand-text">Ribbon Studios</span>
        </Link>

        {/* Desktop navigation */}
        <div className="header-desktop">
          {/* Primary nav */}
          <div className="header-nav-group">
            <HeaderNavLink to="/catalog">Catalog</HeaderNavLink>
            <HeaderNavLink to="/quote">Get a Quote</HeaderNavLink>
            <HeaderNavLink to="/about">About</HeaderNavLink>
          </div>

          {isAuthenticated ? (
            <>
              <div className="header-separator" aria-hidden="true" />

              {/* Utilities and user */}
              <div className="header-user-area">
                <ThemeToggle />
                <HeaderNavLink to="/profile">Profile</HeaderNavLink>
              </div>
            </>
          ) : (
            <>
              <div className="header-separator" aria-hidden="true" />
              <div className="header-nav-group">
                <ThemeToggle />
                <HeaderNavLink to="/login">Sign In</HeaderNavLink>
                <Link
                  to="/login"
                  state={{ authView: 'signup' }}
                  className="header-signup focus-ring"
                >
                  Sign Up
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Mobile hamburger button */}
        <button
          type="button"
          onClick={toggleMenu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          className={`header-hamburger focus-ring${menuOpen ? ' header-hamburger--open' : ''}`}
        >
          <span className="hamburger-line hamburger-line--top" />
          <span className="hamburger-line hamburger-line--mid" />
          <span className="hamburger-line hamburger-line--bot" />
        </button>
      </nav>

      {/* Mobile nav overlay */}
      <div
        className={`mobile-nav-overlay${menuOpen ? ' mobile-nav-overlay--open' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Mobile nav panel */}
      <div
        className={`mobile-nav-panel${menuOpen ? ' mobile-nav-panel--open' : ''}`}
        role="dialog"
        aria-modal={menuOpen || undefined}
        aria-label="Navigation menu"
        inert={!menuOpen || undefined}
      >
        <div style={{ padding: '0 var(--space-5) var(--space-5)', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Close button — positioned to match hamburger in header */}
          <button
            type="button"
            onClick={closeMenu}
            aria-label="Close menu"
            className="focus-ring mobile-nav-close"
            style={{
              position: 'absolute',
              top: 'var(--space-3)',
              right: 'var(--container-padding)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 'var(--space-2)',
              color: 'var(--text-inverse)',
              minHeight: '44px',
              minWidth: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>

          {/* Top spacing to clear close button */}
          <div style={{ height: 'calc(var(--header-height))', flexShrink: 0 }} />

          {/* Nav links */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <MobileNavLink to="/catalog">Catalog</MobileNavLink>
            <MobileNavLink to="/quote">Get a Quote</MobileNavLink>
            <MobileNavLink to="/about">About</MobileNavLink>

          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Bottom auth area */}
          <div className="mobile-nav-bottom" style={{ paddingBottom: 'var(--space-3)' }}>
            <ThemeToggle variant="full" />
          </div>
          {isAuthenticated ? (
            <div
              className="mobile-nav-bottom"
              style={{
                borderTop: '1px solid var(--color-neutral-800)',
                paddingTop: 'var(--space-4)',
              }}
            >
              <Link
                to="/profile"
                onClick={closeMenu}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-neutral-700)',
                  background: 'none',
                  color: 'var(--text-inverse)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  minHeight: '44px',
                  lineHeight: '44px',
                  boxSizing: 'border-box',
                  marginBottom: 'var(--space-3)',
                }}
              >
                My Profile
              </Link>
              <button
                type="button"
                onClick={() => { signOut(); closeMenu(); }}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-neutral-700)',
                  background: 'none',
                  color: 'var(--color-neutral-300)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                Sign Out
              </button>
            </div>
          ) : (
            <div
              className="mobile-nav-bottom"
              style={{
                borderTop: '1px solid var(--color-neutral-800)',
                paddingTop: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
              }}
            >
              <Link
                to="/login"
                onClick={closeMenu}
                style={{
                  display: 'block',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-neutral-700)',
                  color: 'var(--text-inverse)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  minHeight: '44px',
                  lineHeight: '44px',
                  boxSizing: 'border-box',
                }}
              >
                Sign In
              </Link>
              <Link
                to="/login"
                state={{ authView: 'signup' }}
                onClick={closeMenu}
                style={{
                  display: 'block',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-primary-600)',
                  color: 'var(--text-inverse)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  minHeight: '44px',
                  lineHeight: '44px',
                  boxSizing: 'border-box',
                }}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const HeaderNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="header-nav-link focus-ring">
    {children}
  </Link>
);

const MobileNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="mobile-nav-link">
    {children}
  </Link>
);
